import { useClientPortalAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import {
  BasicButton,
  Container,
  Dialog,
  Layout,
  Loader,
  PrimaryButton,
  SecondaryTopNav,
  SectionMessage,
  Text,
} from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { OrderingCheckCard } from "../../../../components/ordering-check-card";
import { valuesMatch } from "../../../../components/ordering-check-card/utils";
import { useCanViewClientPortalPricing } from "../../../../components/permissions/Permissions";
import { formatNullableCurrency } from "../../../../utils/formatNullableCurrency";
import { SelectedBundlesType } from "../../OrderContext";
import { BundleData } from "../../types";

export interface Props {
  bundle?: BundleData;
  isBundleApplied: boolean;
  onClose: () => void;
  onAdd: (bundle?: SelectedBundlesType) => void;
  onRemove: (bundle?: SelectedBundlesType) => void;
  handleUpdateArguments: (
    check_arguments: Record<string, unknown>,
    check: components["schemas"]["BundledDealCheckListChild"]
  ) => void;
}

export function BundleDetails({
  bundle,
  isBundleApplied,
  onClose,
  onAdd,
  onRemove,
  handleUpdateArguments,
}: Props) {
  const { t } = useTranslation();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();
  const { data: bundleDetails, isLoading } = useClientPortalAPI(
    bundle?.id
      ? `/api/internal/client-portal/bundled-deals/${bundle.id}/`
      : null
  );

  const bundlePrice = formatNullableCurrency(
    bundleDetails?.price ?? bundle?.total,
    {
      currency: bundleDetails?.currency || bundle?.currency || "CAD",
      locale: "en-CA",
      fallback: null,
    }
  );
  const bundleCheckTypesWithArguments = (bundle?.check_types_with_arguments ??
    {}) as Record<string, Record<string, unknown>>;

  return (
    <Dialog width="100%" aria-label={t("Bundle details")}>
      <Layout
        layout="applicant/narrow"
        height="calc(100vh - 2.5rem)"
        header={
          <SecondaryTopNav
            testID="bundle-details-close-button"
            leftIcon="close"
            onleftIconOnPress={onClose}
          >
            {isBundleApplied ? (
              <BasicButton
                isDisabled={isLoading || !bundleDetails}
                onPress={() =>
                  onRemove({
                    ...bundleDetails,
                    ...bundle,
                  } as unknown as SelectedBundlesType)
                }
                testID="bundle-details-remove-bundle-button"
              >
                {t("Remove bundled deal")}
              </BasicButton>
            ) : (
              <PrimaryButton
                isDisabled={isLoading || !bundleDetails}
                onPress={() =>
                  onAdd({
                    ...bundleDetails,
                    ...bundle,
                  } as unknown as SelectedBundlesType)
                }
                testID="bundle-details-add-bundle-button"
              >
                {t("Add bundled deal")}
              </PrimaryButton>
            )}
          </SecondaryTopNav>
        }
      >
        <Container layout="applicant/narrow">
          {isBundleApplied && (
            <SectionMessage
              sectionTitle={t("Bundle deal added")}
              type="warning"
              icon="check"
            />
          )}
          <div className={styles.heading}>
            <Text style="summary/section-title" color="text-accent-orange">
              {t("BUNDLED DEAL")}
            </Text>
            <Text style="global/title-small">
              {bundleDetails?.title || bundle?.title || "--"}
            </Text>
            <Text style="global/heading-small" color="text-warning">
              {canViewClientPortalPricing && bundlePrice
                ? `${t("From ")}${bundlePrice}`
                : ""}
            </Text>
          </div>
          <div className={styles.checks}>
            <Text style="global/heading-small">{t("Checks included")}</Text>
            {isLoading ? (
              <Loader />
            ) : (
              bundleDetails?.checks.map((check) => {
                const checkArguments =
                  bundleCheckTypesWithArguments[check.type] ?? {};
                const checkSelectOptions = check.select_options as
                  | components["schemas"]["UmbrellaCheckSelectOption"][]
                  | undefined;
                const selectedOptions = checkSelectOptions?.filter((option) => {
                  const selectFieldArgument = option.select_field_argument;
                  if (!selectFieldArgument) {
                    return false;
                  }

                  return valuesMatch(
                    option.value,
                    checkArguments[selectFieldArgument]
                  );
                });

                const onUpdateSelection = (
                  updatedCheckArguments: Record<string, unknown>
                ) => {
                  handleUpdateArguments(updatedCheckArguments, check);
                };

                return (
                  <OrderingCheckCard
                    key={check.type}
                    price={check.price}
                    currency={check.currency}
                    isDisabled
                    isSelected
                    name={check.name}
                    type={check.type}
                    turnaroundTime={check.estimated_turnaround_time}
                    disclaimersIconList={check.disclaimers_icon_list}
                    disclaimers={check.disclaimers}
                    description={check.description}
                    infoParagraphs={check.info_paragraphs}
                    coverage={check.coverage}
                    keyFeatures={check.key_features}
                    howItCanHelpYou={check.how_it_can_help_you}
                    umbrellaChecksSupported={[]}
                    onUpdateSelection={onUpdateSelection}
                    testID={slugify(check.type)}
                    checkChildren={check.children}
                    selectFieldEndpoint={check.select_field_endpoint}
                    selectOptions={
                      check.select_options as components["schemas"]["UmbrellaCheckSelectOption"][]
                    }
                    selectedOptions={selectedOptions}
                    checkArguments={checkArguments}
                  />
                );
              })
            )}
          </div>
        </Container>
      </Layout>
    </Dialog>
  );
}

const styles = {
  heading: css({
    display: "flex",
    flexDirection: "column",
  }),
  checks: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
  }),
};
